import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, ConfirmDialog } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { GettingStartedDialog } from "~/pages/GettingStarted/GettingStartedDialog";
import { LeftSideContent } from "~/pages/GettingStarted/LeftSideContent";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";

export const CreateYourFirstProjectDialog = ({ open, onClose, isAlreadyCompleted, setProjectId }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState("first");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const showTestimonials = useSelector((state) => !getFeatureFlag(state, "why_this_matters_off"));

  const resetMenu = useCallback(() => {
    setStep("first");
  }, []);

  const onBackButtonClick = useCallback(() => {
    if (isFormDirty) {
      return setShowConfirmDialog(true);
    }

    if (step === "project-form-from-template") {
      return setStep("template");
    }

    return resetMenu();
  }, [isFormDirty, resetMenu, step]);

  const featureExplainerContent = useMemo(() => {
    return (
      <>
        <Body2 className="tw-mt-4">
          {t(
            "Managing projects in ActiveCollab will help you structure and prioritize work, coordinate team efforts, and track progress throughout a project life cycle. With key metrics available at a click, you’ll easily stay on top of things, ensuring projects are delivered on time and within budget."
          )}
        </Body2>
        <Body2 className="tw-mt-4">
          {t(
            "Plus, by onboarding clients, you'll streamline communication, enhance transparency, and foster stronger relationships from day one."
          )}
        </Body2>
      </>
    );
  }, [t]);

  const testimonialContent = useMemo(() => {
    return (
      <>
        <Body2 italic>
          &quot;
          {t(
            "ActiveCollab enables us to centralize our projects and establish work processes that save us a lot of time and money."
          )}
          &quot;
        </Body2>
        <Body2
          weight="bold"
          className="tw-mt-4"
        >
          Thomaskutty Sebastian
        </Body2>
        <Body2>CSO & Partner, PIT Solutions</Body2>
      </>
    );
  }, [t]);

  return (
    <>
      <GettingStartedDialog
        open={open}
        onClose={onClose}
        fullDialogWidth={classnames({ "lg:tw-w-[1000px]": showTestimonials })}
        dialogClassName={classnames("tw-gap-4 lg:tw-gap-[44px] tw-py-4 tw-px-10", {
          "lg:tw-pr-4 sm:tw-py-6 lg:tw-py-4": showTestimonials,
          "md:tw-py-4": !showTestimonials,
        })}
        leftSideClassName="lg:tw-max-w-[500px]"
        rightSideClassName="lg:tw-max-w-[400px]"
        backButtonClassname={{ "tw-mt-1 lg:tw-mt-[11px]": step !== "first", "tw-mt-2 lg:tw-mt-4": step === "first" }}
        showBackButton={step !== "first"}
        onBackButtonClick={onBackButtonClick}
        dialogTitle={t("Create Project")}
        featureExplainerTitle={t("Why this matters")}
        featureExplainerContent={featureExplainerContent}
        testimonialImageUrl="onboarding/images/ThomaskuttySebastian.png"
        testimonialImageAlt="Thomaskutty Sebastian"
        testimonialContent={testimonialContent}
      >
        <LeftSideContent
          step={step}
          setStep={setStep}
          onClose={onClose}
          setFormState={setIsFormDirty}
          isAlreadyCompleted={isAlreadyCompleted}
          setProjectId={setProjectId}
        />
      </GettingStartedDialog>
      {showConfirmDialog ? (
        <ConfirmDialog
          open={showConfirmDialog}
          dialogTitle={t("Go Back")}
          dialogContent={t("All unsaved changes will be lost. Are you sure you want to go back?")}
          confirmBtnText={t("Confirm")}
          onCancel={() => {
            setShowConfirmDialog(false);
          }}
          onConfirm={() => {
            setIsFormDirty(false);
            setShowConfirmDialog(false);
            resetMenu();
          }}
        />
      ) : null}
    </>
  );
};

CreateYourFirstProjectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  isAlreadyCompleted: PropTypes.bool,
  setProjectId: PropTypes.func,
};

export default CreateYourFirstProjectDialog;
